'use client'

import { ButtonBase } from '@fukku/button/Base/ButtonBase'
import { useHorizontalScroll } from '@fukku/horizontal-scroll/hooks/useHorizontalScroll/useHorizontalScroll'
import { ClientTextLink } from '@fukku/text-link/client/ClientTextLink'
import { LinkType } from '@fukku/text-link/utils'
import { cx } from 'classix'
import { IconDropdownNextSmall } from 'icons/components/IconDropdownNextSmall'
import { IconDropdownPrevSmall } from 'icons/components/IconDropdownPrevSmall'
import type { SeoBannerLinkDisplay } from './SeoBanner'
import styles from './SeoBanner.module.css'

interface SeoBannerListProps {
	readonly seoLinks: SeoBannerLinkDisplay[]
	readonly brandLabel: string
	readonly previousButtonLabel: string
	readonly nextButtonLabel: string
}

const id = 'seoBanner'

export function SeoBannerList({
	seoLinks,
	brandLabel,
	previousButtonLabel,
	nextButtonLabel,
}: SeoBannerListProps): React.ReactNode {
	const {
		horizontalScrollRef,
		horizontalScrollEvents,
		handleLeftScroll,
		handleRightScroll,
		isAtStart,
		isAtEnd,
	} = useHorizontalScroll()

	return (
		<div className={styles.scrollWrapper}>
			{!isAtStart && (
				<ButtonBase
					onClick={handleLeftScroll}
					className={cx(styles.button, styles.buttonPrevious)}
					disabled={isAtStart}
					aria-label={previousButtonLabel}
					data-testid={`seoBanner.${id}.previous.button`}
				>
					<IconDropdownPrevSmall className={styles.buttonIcon} />
				</ButtonBase>
			)}
			<ul
				{...horizontalScrollEvents}
				className={styles.linkList}
				ref={horizontalScrollRef as React.RefObject<HTMLUListElement>}
				data-testid={`seoBanner.${id}.container`}
				id={id}
			>
				{seoLinks.map((seoLink) => {
					const linkTitle = `${seoLink.label} ${brandLabel}`
					return (
						<li key={seoLink.href} className={styles.listEl}>
							<ClientTextLink
								type={LinkType.Inspirational}
								href={seoLink.href}
								className={styles.link}
								linkProps={{ title: linkTitle, 'aria-label': linkTitle }}
								data-testid='landings.home.seoBannerLink'
							>
								{seoLink.label}
							</ClientTextLink>
						</li>
					)
				})}
			</ul>
			{!isAtEnd && (
				<ButtonBase
					onClick={handleRightScroll}
					className={cx(styles.button, styles.buttonNext)}
					disabled={isAtEnd}
					aria-label={nextButtonLabel}
					data-testid={`seoBanner.${id}.next.button`}
				>
					<IconDropdownNextSmall className={styles.buttonIcon} />
				</ButtonBase>
			)}
		</div>
	)
}
